import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiUrl } from '../../utils/api';
import { Coins, Save } from 'lucide-react';

const API_URL = getApiUrl();

// V42.7 — Configurable FX→INR rates used ONLY for the Sales Dashboard's blended
// "Total revenue (₹-equivalent)" line. Native per-currency cards stay exact.
const FxRatesCard = () => {
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/settings/fx-rates`, { withCredentials: true });
      setRates(res.data.rates || {});
      setUpdatedAt(res.data.updated_at || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load FX rates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setRate = (cur, val) => setRates((r) => ({ ...r, [cur]: val }));

  const save = async () => {
    const clean = {};
    for (const [cur, v] of Object.entries(rates)) {
      const fv = parseFloat(v);
      if (!isNaN(fv) && fv > 0) clean[cur] = fv;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/settings/fx-rates`, { rates: clean }, { withCredentials: true });
      toast.success('FX rates saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save FX rates');
    } finally {
      setSaving(false);
    }
  };

  const currencies = Object.keys(rates).sort();

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="fx-rates-card">
      <div className="flex items-center gap-2 mb-1">
        <Coins className="w-5 h-5 text-amber-600" />
        <h3 className="text-lg font-semibold text-slate-900">FX Rates (→ INR)</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Used only for the Sales Dashboard's blended <strong>"Total revenue (₹-equivalent)"</strong> line.
        The native per-currency cards always stay exact (no conversion). 1 unit of each currency = N ₹.
      </p>

      {loading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {currencies.map((cur) => (
              <div key={cur} className="flex items-center gap-2">
                <span className="w-12 text-sm font-medium text-slate-700">{cur}</span>
                <div className="relative flex-1">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={rates[cur]}
                    onChange={(e) => setRate(cur, e.target.value)}
                    data-testid={`fx-rate-input-${cur}`}
                    className="w-full pl-2 pr-7 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">₹</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={save}
              disabled={saving}
              data-testid="fx-rates-save-btn"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save FX rates'}
            </button>
            {updatedAt && (
              <span className="text-xs text-slate-400">
                Last updated {new Date(updatedAt).toLocaleString('en-IN')}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FxRatesCard;
